import { useState, useCallback, useEffect } from 'react';

interface IAnimationState {
  isEntering: boolean;
  isExiting: boolean;
  animationClass: string;
}

export const useWizardAnimations = (isTransitioning: boolean) => {
  const [animationState, setAnimationState] = useState<IAnimationState>({
    isEntering: false,
    isExiting: false,
    animationClass: '',
  });

  // Handle step transition animations
  useEffect(() => {
    if (isTransitioning) {
      // Start exit animation
      setAnimationState({
        isEntering: false,
        isExiting: true,
        animationClass: 'step-exit',
      });

      // After exit animation, start enter animation
      const exitTimer = setTimeout(() => {
        setAnimationState({
          isEntering: true,
          isExiting: false,
          animationClass: 'step-enter',
        });

        // Clear enter state after animation completes
        const enterTimer = setTimeout(() => {
          setAnimationState({
            isEntering: false,
            isExiting: false,
            animationClass: '',
          });
        }, 300);

        return () => clearTimeout(enterTimer);
      }, 300);

      return () => clearTimeout(exitTimer);
    }
  }, [isTransitioning]);

  // Card stagger animation delays
  const getCardDelay = useCallback((index: number): string => {
    return `${0.1 + index * 0.1}s`;
  }, []);

  // Get animation class for cards with stagger
  const getCardAnimationClass = useCallback((index: number): string => {
    if (animationState.isEntering) {
      return `card-slide-in`;
    }
    return '';
  }, [animationState.isEntering]);

  // Button loading animation
  const [buttonLoadingStates, setButtonLoadingStates] = useState<{ [key: string]: boolean }>({});

  const setButtonLoading = useCallback((buttonId: string, loading: boolean) => {
    setButtonLoadingStates(prev => ({
      ...prev,
      [buttonId]: loading,
    }));
  }, []);

  const isButtonLoading = useCallback((buttonId: string): boolean => {
    return buttonLoadingStates[buttonId] || false;
  }, [buttonLoadingStates]);

  // Success animation trigger
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const triggerSuccessAnimation = useCallback(() => {
    setShowSuccessAnimation(true);
  }, []);

  return {
    // Animation state
    ...animationState,
    
    // Card animations
    getCardDelay,
    getCardAnimationClass,
    
    // Button animations
    setButtonLoading,
    isButtonLoading,
    
    // Success animation
    showSuccessAnimation,
    triggerSuccessAnimation,
  };
};